import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import authService from '../services/authService'
import { handleInvalidSession } from '../services/parseCloud'
import type { AuthUser } from '../core/models/User'

export type SessionContextValue = {
  /** True after the boot-time session check has settled (success or failure). */
  sessionReady: boolean
  /** User from session check, or null when unauthenticated / invalid session. */
  sessionUser: AuthUser | null
  /** Re-check the session the cheap way (e.g. after tab focus); at most one request a minute. */
  revalidateSession: () => Promise<AuthUser | null>
}

const SessionContext = createContext<SessionContextValue>({
  sessionReady: false,
  sessionUser: null,
  revalidateSession: async () => null,
})

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const [sessionReady, setSessionReady] = useState(false)
  const [sessionUser, setSessionUser] = useState<AuthUser | null>(null)

  useEffect(() => {
    let cancelled = false

    void authService.waitForSessionCheck().then((user) => {
      if (cancelled) return
      setSessionUser(user)
      setSessionReady(true)
    })

    return () => {
      cancelled = true
    }
  }, [])

  // Debounced revalidation when tab becomes visible again
  useEffect(() => {
    if (typeof document === 'undefined') return

    let debounceTimer: ReturnType<typeof setTimeout> | null = null

    const revalidate = async () => {
      // The light check (one request at most, once a minute) keeps tab flipping cheap, and
      // authService announces `sessionExpired` itself when Parse really rejects the session.
      // Watching for that event tells us whether the handler below still has to announce it.
      let announced = false
      const onExpired = () => {
        announced = true
      }
      window.addEventListener('sessionExpired', onExpired)
      try {
        const user = await authService.checkSessionLight()
        setSessionUser(user)
        // authService only dropped its own copy of the session. The menu, Redux and the other
        // per-user caches are reset by the same handler a refused cloud call uses (it also
        // leaves a player in a room where they are). Announced once per expiry, not twice.
        if (!user) void handleInvalidSession({ sessionExpiredAnnounced: announced })
      } finally {
        window.removeEventListener('sessionExpired', onExpired)
      }
    }

    const onVisibility = () => {
      // Asked when the tab returns, not taken from `sessionUser`: that is set once at boot, so a
      // user who signed in later never had the check. Signed out, there is nothing to check, and
      // the check's "no session" answer would announce an expiry to someone who never had one.
      if (document.visibilityState !== 'visible' || !authService.getCurrentUser()) return
      if (debounceTimer) clearTimeout(debounceTimer)
      debounceTimer = setTimeout(() => {
        void revalidate()
      }, 500)
    }

    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      document.removeEventListener('visibilitychange', onVisibility)
      if (debounceTimer) clearTimeout(debounceTimer)
    }
  }, [])

  const value = useMemo<SessionContextValue>(
    () => ({
      sessionReady,
      sessionUser,
      revalidateSession: () => authService.checkSessionLight(),
    }),
    [sessionReady, sessionUser]
  )

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

export function useSession(): SessionContextValue {
  return useContext(SessionContext)
}
